import React, { useEffect, useRef, useState } from 'react';
import { Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, Card, CardContent, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography } from '@mui/material';
import { useEventEdition } from '../../contexts/EventEditionContext';
import { downloadKutcResults, loadKutcResultExport, ResultExportPreview, saveKutcExportIdentity } from '../../services/kutcResultExportService';
import { correctExportRow, exportBirthdate, exportGender, ExportIdentityCorrection, ExportResultRow, RESULT_EXPORT_HEADERS, utmbExportRows, validateIdentityCorrection } from '../../utils/kutcResultExport';
import { isoToIoc } from '../../constants/iocCountryCodes';

import KUTCDuvExportPanel from './KUTCDuvExportPanel';

interface IdentityEditor {
  editionId: string;
  personId: number;
  name: string;
  value: ExportIdentityCorrection;
}

const KUTCResultExportPanel: React.FC = () => {
  const { event } = useEventEdition();
  const editionId = event?.id;
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [target, setTarget] = useState<'' | 'UTMB' | 'DUV'>('');
  const [duvVisited, setDuvVisited] = useState(false);
  const [preview, setPreview] = useState<ResultExportPreview | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [revision, setRevision] = useState(0);
  const [editing, setEditing] = useState<IdentityEditor | null>(null);
  const [editorError, setEditorError] = useState('');
  const [saving, setSaving] = useState(false);
  const [corrections, setCorrections] = useState<Record<string, 'export' | 'saved'>>({});
  const generation = useRef(0);

  useEffect(() => {
    generation.current += 1;
    let cancelled = false;
    setExpanded({});
    setPreview(null);
    setError('');
    setEditing(null);
    setCorrections({});
    if (!editionId?.startsWith('kutc-')) return;
    setBusy(true);
    loadKutcResultExport(editionId).then(value => {
      if (!cancelled) setPreview(value);
    }).catch(err => {
      if (!cancelled) setError(err instanceof Error ? err.message : 'Kunne ikke hente resultater.');
    }).finally(() => { if (!cancelled) setBusy(false); });
    return () => { cancelled = true; };
  }, [editionId, revision]);

  if (!editionId?.startsWith('kutc-')) return <Alert severity="info">Velg en KUTC-årsutgave for å eksportere resultater.</Alert>;
  const current = preview?.editionId === editionId ? preview : null;
  const isFinal = current?.resultsStatus.trim().toLowerCase() === 'final';
  const editor = editing?.editionId === editionId ? editing : null;
  const targets = editor ? current?.identityTargets?.[editor.personId] : undefined;
  const openEditor = (row: ExportResultRow) => {
    const identity = current?.identities?.[row.personId];
    setEditorError('');
    setEditing({ editionId, personId: row.personId, name: `${row.firstName} ${row.lastName}`, value: {
      gender: exportGender(identity?.gender) || row.gender,
      birthdate: exportBirthdate(identity?.dateOfBirth) || row.birthdate,
      nationality: String(identity?.nationality || '').trim().toUpperCase()
    } });
  };
  const applyCorrection = async (persist: boolean) => {
    if (!editor || !current) return;
    const baseline = current;
    const editGeneration = generation.current;
    setEditorError('');
    try {
      const value = validateIdentityCorrection(editor.value);
      setSaving(true);
      const savedTargets = persist && targets ? await saveKutcExportIdentity(current.editionId, editor.personId, value, targets) : null;
      if (generation.current !== editGeneration) return;
      if (persist && !savedTargets) throw new Error('Fant ingen profilkobling å lagre til.');
      setPreview(previous => previous !== baseline ? previous : {
        ...previous,
        identities: { ...previous.identities, [editor.personId]: { gender: value.gender, dateOfBirth: value.birthdate, nationality: value.nationality } },
        identityTargets: { ...previous.identityTargets, ...(savedTargets ? { [editor.personId]: savedTargets } : {}) },
        races: previous.races.map(race => ({ ...race, rows: race.rows.map(row => row.personId === editor.personId ? correctExportRow(row, value) : row) }))
      });
      setCorrections(previous => ({ ...previous, [editor.personId]: persist ? 'saved' : 'export' }));
      setEditing(previous => previous === editing ? null : previous);
    } catch (err) {
      if (generation.current === editGeneration) setEditorError(err instanceof Error ? err.message : 'Kunne ikke bruke rettelsen.');
    } finally { setSaving(false); }
  };
  return <Stack spacing={2}>
    <Typography variant="h5">KUTC – resultateksport</Typography>
    <TextField select label="Mottaker" value={target} SelectProps={{ native: true }} disabled={busy || downloading || saving || Boolean(editor)} onChange={e => {
      const next = e.target.value as typeof target; setTarget(next); if (next === 'DUV') setDuvVisited(true);
    }} sx={{ maxWidth: 360 }}>
      <option value="">Velg resultateksport</option><option value="UTMB">UTMB</option><option value="DUV">DUV</option>
    </TextField>
    <Typography>Årsutgave: {editionId}</Typography>
    {target === 'UTMB' && <Alert severity="info">UTMB: Én fil per distanse, fullførte og DNF. DNS og totalkonkurransen utelates.</Alert>}
    {!target && <Alert severity="info">Velg mottaker for å vise resultater og nedlastinger.</Alert>}
    <Button onClick={() => setRevision(value => value + 1)} disabled={busy || downloading || Boolean(editor) || saving} sx={{ alignSelf: 'flex-start' }}>Hent oppdaterte resultater</Button>
    {Object.values(corrections).includes('export') && <Alert severity="info">Du har rettelser som bare brukes i denne eksportøkten. De forsvinner ved ny innlasting, bytte av årsutgave eller når du forlater visningen.</Alert>}
    {busy && <CircularProgress size={24} />}
    {error && <Alert severity="error">{error}</Alert>}
    {current && !isFinal && <Alert severity="warning">Resultatstatus er «{current.resultsStatus}». Eksport åpnes når årsutgavens resultatstatus er «final».</Alert>}
    {current && !current.races.length && <Alert severity="warning">Ingen distanser funnet for denne årsutgaven.</Alert>}
    <Box hidden={target !== 'UTMB'}>
    <Typography variant="h5" component="h2" sx={{ mb: 2 }}>UTMB-resultater</Typography>
    {current?.races.map(race => {
      // Unknown statuses must remain visible and block export rather than silently vanish.
      const rows = utmbExportRows(race.rows);
      const invalid = rows.some(row => row.issues.length);
      return <Card key={race.distanceKey} variant="outlined"><CardContent>
        <Stack spacing={2}>
          <Typography variant="h6">{race.name} – {rows.length} rader</Typography>
          <Typography variant="body2">{race.rows.filter(row => row.status === 'Finished').length} fullførte · {race.rows.filter(row => row.status === 'DNF').length} DNF · {race.rows.filter(row => row.status === 'DNS').length} DNS</Typography>
          {invalid && <Alert severity="error">Rett opplysningene som er merket i tabellen før nedlasting.</Alert>}
          {!rows.length && <Alert severity="info">Ingen resultater med valgt utvalg. Det lages ingen tom fil.</Alert>}
          <Button variant="contained" disabled={!isFinal || invalid || !rows.length || busy || downloading || saving || Boolean(editor)} sx={{ alignSelf: 'flex-start' }} onClick={async () => {
            setDownloading(true); setError('');
            try { await downloadKutcResults(current.editionId, race, rows); }
            catch (err) { setError(err instanceof Error ? err.message : 'Eksport feilet.'); }
            finally { setDownloading(false); }
          }}>Last ned UTMB Excel – {race.name}</Button>
          <Accordion expanded={expanded[race.distanceKey] ?? invalid} onChange={(_event, open) => setExpanded(previous => ({ ...previous, [race.distanceKey]: open }))} disableGutters>
            <AccordionSummary aria-label={`Vis resultater – ${race.name}`} expandIcon={<span>⌄</span>}><Typography>Vis resultater – {race.name}</Typography></AccordionSummary>
            <AccordionDetails>
          <Box sx={{ overflowX: 'auto' }}><Table size="small">
            <TableHead><TableRow>{[...RESULT_EXPORT_HEADERS, 'Kontroll'].map(header => <TableCell key={header}>{header}</TableCell>)}</TableRow></TableHead>
            <TableBody>{rows.map((row, index) => <TableRow key={`${row.personId}-${index}`}>
              {[row.rank, row.time, row.lastName, row.firstName, row.birthdate, row.gender, row.nationality, row.bib].map((value, cell) => <TableCell key={cell}>{value}</TableCell>)}
              <TableCell sx={{ color: row.issues.length ? 'error.main' : 'text.secondary' }}>
                {row.issues.join('; ') || 'OK'}
                {corrections[row.personId] && <Typography variant="caption" display="block">{corrections[row.personId] === 'saved' ? 'Lagret i RunnersHub' : 'Bare denne eksporten'}</Typography>}
                <Button size="small" disabled={busy || downloading || saving} onClick={() => openEditor(row)} aria-label={`Rediger identitet for ${row.firstName} ${row.lastName}`}>Rediger identitet</Button>
              </TableCell>
            </TableRow>)}</TableBody>
          </Table></Box></AccordionDetails></Accordion>
        </Stack>
      </CardContent></Card>;
    })}
    </Box>
    {duvVisited && current && event && <Box hidden={target !== 'DUV'}><KUTCDuvExportPanel key={current.editionId} preview={current} event={event} /></Box>}
    <Dialog open={Boolean(editor)} onClose={() => { if (!saving) setEditing(null); }} fullWidth maxWidth="sm">
      <DialogTitle>Kompletter identitet – {editor?.name}</DialogTitle>
      <DialogContent><Stack spacing={2} sx={{ pt: 1 }}>
        <Typography variant="body2">Person-ID {editor?.personId}. Rettelsen brukes på alle distanser for personen i denne eksportøkten.</Typography>
        <TextField select label="Kjønn" value={editor?.value.gender || ''} disabled={saving} onChange={e => setEditing(previous => previous ? { ...previous, value: { ...previous.value, gender: e.target.value } } : null)}>
          <MenuItem value="">Velg kjønn</MenuItem><MenuItem value="M">M – mann</MenuItem><MenuItem value="F">F – kvinne</MenuItem>
        </TextField>
        <TextField label="Fødselsdato" type="date" InputLabelProps={{ shrink: true }} value={editor?.value.birthdate || ''} disabled={saving}
          onChange={e => setEditing(previous => previous ? { ...previous, value: { ...previous.value, birthdate: e.target.value } } : null)} helperText="Full faktisk fødselsdato. Fødselsår alene er ikke tilstrekkelig." />
        <TextField label="Nasjonalitet (ISO-landkode)" value={editor?.value.nationality || ''} disabled={saving}
          onChange={e => setEditing(previous => previous ? { ...previous, value: { ...previous.value, nationality: e.target.value.toUpperCase() } } : null)}
          helperText={`Eksempel: NOR, DNK, DEU. IOC-kode i filen: ${isoToIoc(editor?.value.nationality) || 'ukjent'}`} />
        <Typography variant="body2">«Lagre i RunnersHub» oppdaterer {targets?.profiles.length || 0} tilknyttede profildokumenter og {targets?.registrations.length || 0} påmeldinger for {editionId}, med revisjonsspor. Full fødselsdato publiseres ikke til nye offentlige felt.</Typography>
        {targets?.error && <Alert severity="error">{targets.error}</Alert>}
        {!targets?.profiles.length && <Alert severity="info">Profilkobling mangler. Du kan bruke opplysningene bare i eksporten.</Alert>}
        {editorError && <Alert severity="error">{editorError}</Alert>}
      </Stack></DialogContent>
      <DialogActions>
        <Button disabled={saving} onClick={() => setEditing(null)}>Avbryt</Button>
        <Button disabled={saving} onClick={() => applyCorrection(false)}>Bruk bare i denne eksporten</Button>
        <Button variant="contained" disabled={saving || !targets?.profiles.length || Boolean(targets?.error)} onClick={() => applyCorrection(true)}>Lagre i RunnersHub</Button>
      </DialogActions>
    </Dialog>
  </Stack>;
};

export default KUTCResultExportPanel;
